import React, { useEffect, useRef, useState } from "react";
import { Globe, ExternalLink, RefreshCw } from "lucide-react";
import { buildPreviewURL } from "../lib/api";

/**
 * Preview iframe that loads /api/run-web preview URLs.
 *
 * The iframe carries `sandbox="allow-scripts allow-modals allow-forms"`; the
 * server also sets a CSP `sandbox` directive on the response — both layers
 * apply.
 *
 * `previewUrl` is the relative URL returned by /api/run-web. We append a
 * cache-busting `?t=...` so re-running the same code reloads the iframe.
 */
export default function PreviewPanel({ enabled, previewUrl, runStamp }) {
  const iframeRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  const fullURL = previewUrl
    ? buildPreviewURL(
        `${previewUrl}${previewUrl.includes("?") ? "&" : "?"}t=${runStamp || Date.now()}`
      )
    : null;

  useEffect(() => {
    setLoaded(false);
  }, [previewUrl, runStamp]);

  function handleRefresh() {
    if (!iframeRef.current || !fullURL) return;
    setLoaded(false);
    iframeRef.current.src = `${buildPreviewURL(previewUrl)}${
      previewUrl.includes("?") ? "&" : "?"
    }t=${Date.now()}`;
  }

  return (
    <div className="h-full flex flex-col bg-ide-panel2">
      <div className="flex items-center px-3 py-1.5 border-b border-ide-border text-[11px] uppercase tracking-wider text-ide-mute">
        <span>preview</span>
        {enabled && previewUrl && (
          <span
            className={
              "ml-2 inline-flex items-center gap-1 normal-case px-1.5 py-0.5 rounded-full text-[10px] border " +
              (loaded
                ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
                : "text-ide-mute border-ide-border bg-ide-panel")
            }
          >
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                (loaded
                  ? "bg-emerald-400 animate-pulse-dot"
                  : "bg-ide-mute")
              }
            />
            {loaded ? "live" : "loading"}
          </span>
        )}
        <div className="flex-1" />
        {enabled && previewUrl && (
          <>
            <button
              onClick={handleRefresh}
              className="normal-case flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-ide-panel hover:text-ide-text transition-colors mr-1"
              title="Reload iframe"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              reload
            </button>
            <a
              href={buildPreviewURL(previewUrl)}
              target="_blank"
              rel="noreferrer"
              className="normal-case flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-ide-panel hover:text-ide-text transition-colors"
              title="Open in a new tab"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              open
            </a>
          </>
        )}
      </div>

      {enabled ? (
        previewUrl ? (
          <div className="flex-1 min-h-0 bg-white">
            <iframe
              ref={iframeRef}
              key={runStamp || previewUrl}
              src={fullURL}
              title="noefforts-preview"
              className="h-full w-full border-0"
              sandbox="allow-scripts allow-modals allow-forms"
              onLoad={() => setLoaded(true)}
            />
          </div>
        ) : (
          <div className="flex-1 grid place-items-center text-ide-mute text-sm">
            <div className="text-center max-w-sm">
              <Globe className="h-6 w-6 mx-auto text-sky-400 mb-2" />
              <div className="text-ide-text">Press Run to render the preview</div>
              <div className="text-[12px] mt-1">
                Your HTML/CSS/JS renders inside a sandboxed iframe.
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="flex-1 grid place-items-center text-ide-mute text-sm">
          <div className="text-center max-w-sm">
            <Globe className="h-6 w-6 mx-auto text-ide-mute mb-2" />
            <div className="text-ide-text">No preview for this language</div>
            <div className="text-[12px] mt-1">
              Switch to <span className="font-mono text-ide-text">HTML</span> to see a live preview.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
